import React from 'react';
import { LucideIcon } from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import { Heading, Text } from '../ui/Typography';
import { cn } from '../../utils/cn';

export interface QuickStat {
  title: string;
  value: number | string;
  change?: number;
  trend?: 'up' | 'down' | 'stable';
  icon: React.ComponentType<{ className?: string }>;
}

interface QuickStatsProps {
  stats: QuickStat[];
}

export function QuickStats({ stats }: QuickStatsProps) {
  if (!stats || stats.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {stats.map((stat, index) => {
        const Icon = stat.icon;
        const trendColorClass =
          stat.trend === 'up'
            ? 'text-success'
            : stat.trend === 'down'
            ? 'text-destructive'
            : 'text-text-tertiary';

        return (
          <Card
            key={index}
            className="hover:shadow-md transition-shadow"
            style={{
              transition: 'var(--transition-smooth)',
            }}
          >
            <CardContent>
              <div className="flex items-center justify-between mb-2">
                <div 
                  className="p-2 rounded-lg bg-primary/20 rounded-md"
                >
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                {stat.change !== undefined && (
                  <Text 
                    size="sm" 
                    weight="medium"
                    className={trendColorClass}
                  >
                    {stat.trend === 'up' ? '↑' : stat.trend === 'down' ? '↓' : '→'} {Math.abs(stat.change)}%
                  </Text>
                )}
              </div>
              <div>
                <Heading level={2} className="text-2xl font-bold">
                  {stat.value}
                </Heading>
                <Text size="sm" className="mt-1 text-text-secondary">
                  {stat.title}
                </Text>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

